import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MikaCard from '../../components/MikaCard/MikaCard';
import { formatPrice } from '../../utils/react';
import moment from 'moment';

interface ProductionProductCardGridProps {
    data: any[];
    onEdit: (product: any) => void;
}

const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.charAt(0) + (parts[1]?.charAt(0) || '')).toUpperCase();
};

const getAvatarColor = (name: string): string => {
    const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const ProductionProductCardGrid: React.FC<ProductionProductCardGridProps> = ({ data, onEdit }) => {
    const intl = useIntl();
    const infoTooltip = intl.formatMessage({ id: 'production.products.info', defaultMessage: 'Info' });

    return (
        <div className="ccg-grid">
            {data.length === 0 ? (
                <div className="ccg-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                        <path d="M2 20h20" /><path d="M5 20V10l7-7 7 7v10" /><path d="M9 20v-6h6v6" />
                    </svg>
                    <p><FormattedMessage id="production.products.empty" defaultMessage="Nuk u gjet asnjë produkt" /></p>
                </div>
            ) : (
                data.filter(p => !p._isTotalRow).map((product) => {
                    const initials = getInitials(product.clientName || '');
                    const avatarColor = getAvatarColor(product.clientName || '');

                    return (
                        <MikaCard
                            key={product.productId}
                            onClick={() => onEdit(product)}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: avatarColor }}>
                                        {initials}
                                    </div>
                                    <div className="ccg-card-name">
                                        <span className="ccg-name">{product.productName || '\u2014'}</span>
                                        <span className="ccg-company">{product.clientName}</span>
                                    </div>
                                    <span className="ccg-status-badge" style={{ color: product.statusColor, background: product.statusBg }}>
                                        {product.statusLabel}
                                    </span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>
                                            <span className="ccg-info-value">{product.projectName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.quantity" defaultMessage="Sasia" /></span>
                                            <span className="ccg-info-value">{product.quantity || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.start" defaultMessage="Fillimi" /></span>
                                            <span className="ccg-info-value">{product.start ? moment(product.start).format('DD-MM-YYYY') : '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.end" defaultMessage="Mbarimi" /></span>
                                            <span className="ccg-info-value">{product.end ? moment(product.end).format('DD-MM-YYYY') : '\u2014'}</span>
                                        </div>
                                        {product.totalPrice && (
                                            <div className="ccg-info-item">
                                                <span className="ccg-info-label"><FormattedMessage id="production.detail.value" defaultMessage="Vlera" /></span>
                                                <span className="ccg-info-value" style={{ color: '#1e40af', fontWeight: 600 }}>{formatPrice(product.totalPrice, '€')}</span>
                                            </div>
                                        )}
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.products.column.measure" defaultMessage="Matja" /></span>
                                            <span className="ccg-info-value">{product.measureDone || '\u2014'}</span>
                                        </div>
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date" style={{ color: product.statusColor }}>
                                        {product.statusLabel}
                                    </span>
                                    <div className="ccg-footer-actions">
                                        <button className="ccg-action-btn" onClick={(e) => { e.stopPropagation(); onEdit(product); }} title={infoTooltip}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            }
                        />
                    );
                })
            )}
        </div>
    );
};

export default ProductionProductCardGrid;
